// Playback copies made on this device (HRTH-105.8, docs/STORAGE.md §3): the original is
// always kept as it is; a copy is an extra blob for streaming. Audio becomes Opus in Ogg,
// video fragmented MP4. Tracks whose codec this browser plays are copied without
// re-encoding; the rest are re-encoded with the browser's own encoders (WebCodecs, through
// Mediabunny). We never ship an encoder of our own. Copies are written to the origin-private
// file system where there is one, so a long film never sits in memory and a reload can
// carry on uploading the same copy.

import type { StreamTargetChunk } from 'mediabunny'

/**
 * When to make a playback copy: only when browsers can't play the original, also a lighter
 * copy of large originals (lossless audio, 4K video), or never.
 */
export type CopyMode = 'auto' | 'lighter' | 'original'

export const COPY_MODES: { mode: CopyMode; label: string; hint: string }[] = [
  { mode: 'auto', label: 'Automatic', hint: 'A copy only when browsers can’t play the original' },
  { mode: 'lighter', label: 'Lighter copy too', hint: 'Also a lighter copy of lossless audio and large video, for phones and slow links' },
  { mode: 'original', label: 'Original only', hint: 'No copies; files browsers can’t play can still be downloaded' },
]

/** What a file holds, read on this device. */
export interface SourceDetails {
  audio?: string | null
  video?: string | null
  height?: number
  duration?: number
}

export interface CopyPlan {
  kind: 'audio' | 'video'
  why: 'unplayable' | 'lighter'
  /** Scale video down to this height. */
  maxHeight?: number
}

export interface MadeCopy {
  blob: Blob
  mime: string
  /** Where it's kept on this device, if it is (the origin-private file system). */
  name?: string
}

export interface CopyMaker {
  details(file: Blob): Promise<SourceDetails | null>
  /** The copy, or null if this browser can't read or decode the file. */
  make(file: Blob, plan: CopyPlan, opts: { onProgress?: (f: number) => void; signal?: AbortSignal }): Promise<MadeCopy | null>
  /** A copy kept on this device, if it's still there. */
  open(name: string): Promise<Blob | null>
  remove(name: string): Promise<void>
  /** Removes kept copies older than a day that nothing refers to. */
  prune(keep: string[]): Promise<void>
}

/**
 * What every browser plays (Chrome, Firefox, Safari): the browser that imports a file may
 * play more (Firefox on Linux plays HEVC and MKV), but the people it's shared with may not.
 */
const PORTABLE_TYPES = /^(video\/(mp4|webm)|audio\/(mpeg|mp4|aac|ogg|opus|flac|wav|x-wav|wave|webm))$/
const PORTABLE_VIDEO = new Set(['avc', 'vp8', 'vp9', 'av1'])
const PORTABLE_AUDIO = /^(aac|mp3|opus|vorbis|flac|pcm-)/

/** Whether every browser plays a file, as far as its type and codecs tell. */
export function portable(mime: string, d: SourceDetails): boolean {
  return PORTABLE_TYPES.test(mime) && (!d.video || PORTABLE_VIDEO.has(d.video)) && (!d.audio || PORTABLE_AUDIO.test(d.audio))
}

const LOSSLESS = /^(flac|pcm-)/
/** Above these, a lighter copy is worth making. */
const LIGHT_AUDIO_BPS = 400_000
const LIGHT_VIDEO_BPS = 12_000_000
const LIGHT_VIDEO_HEIGHT = 1080

/**
 * Whether a file gets a copy, and what kind. `playable` is whether this browser plays it;
 * `details` (when the file could be read) tell whether every browser does.
 */
export function planCopy(
  src: { kind: 'audio' | 'video'; mime: string; playable: boolean; size: number },
  mode: CopyMode,
  details?: SourceDetails | null,
): CopyPlan | null {
  if (mode === 'original') return null
  if (!src.playable || (details && !portable(src.mime, details))) return { kind: src.kind, why: 'unplayable' }
  if (mode !== 'lighter' || !details) return null
  const bps = details.duration ? (src.size * 8) / details.duration : 0
  if (src.kind === 'audio') return LOSSLESS.test(details.audio ?? '') || bps > LIGHT_AUDIO_BPS ? { kind: 'audio', why: 'lighter' } : null
  if ((details.height ?? 0) > LIGHT_VIDEO_HEIGHT) return { kind: 'video', why: 'lighter', maxHeight: LIGHT_VIDEO_HEIGHT }
  return bps > LIGHT_VIDEO_BPS ? { kind: 'video', why: 'lighter' } : null
}

// --- In the browser --------------------------------------------------------------------

const DIR = 'hrth-copies'
const DAY = 24 * 60 * 60 * 1000

async function copiesDir(): Promise<FileSystemDirectoryHandle | null> {
  try {
    return await (await navigator.storage.getDirectory()).getDirectoryHandle(DIR, { create: true })
  } catch {
    return null
  }
}

interface Sink {
  name?: string
  writable: WritableStream<StreamTargetChunk>
  result(): Promise<Blob>
  remove(): Promise<void>
}

/** Somewhere to write a copy: a file in the origin-private file system, else memory. */
async function openSink(): Promise<Sink> {
  const dir = await copiesDir()
  if (dir) {
    try {
      const name = crypto.randomUUID()
      const handle = await dir.getFileHandle(name, { create: true })
      const w = await handle.createWritable()
      return {
        name,
        writable: new WritableStream({
          write: (c) => w.write({ type: 'write', position: c.position, data: c.data }),
          close: () => w.close(),
          abort: () => w.abort(),
        }),
        result: () => handle.getFile(),
        remove: async () => {
          await w.abort().catch(() => undefined)
          await dir.removeEntry(name).catch(() => undefined)
        },
      }
    } catch {
      // No writable files here (older Safari): fall back to memory.
    }
  }
  let parts: StreamTargetChunk[] = []
  return {
    writable: new WritableStream({ write: (c) => void parts.push({ ...c, data: c.data.slice() }) }),
    result: async () => {
      const size = parts.reduce((n, c) => Math.max(n, c.position + c.data.length), 0)
      const all = new Uint8Array(size)
      for (const c of parts) all.set(c.data, c.position)
      return new Blob([all])
    },
    remove: async () => void (parts = []),
  }
}

const plays = (type: string) => {
  const v = document.createElement('video')
  return v.canPlayType(type) !== ''
}

const VIDEO_TARGETS = [
  { codec: 'avc', type: 'video/mp4; codecs="avc1.640028"' },
  { codec: 'vp9', type: 'video/mp4; codecs="vp09.00.40.08"' },
  { codec: 'av1', type: 'video/mp4; codecs="av01.0.08M.08"' },
] as const
const AUDIO_TARGETS = [
  { codec: 'aac', type: 'audio/mp4; codecs="mp4a.40.2"' },
  { codec: 'opus', type: 'audio/mp4; codecs="opus"' },
] as const

export const browserCopies: CopyMaker = {
  async details(file) {
    const mb = await import('mediabunny')
    const input = new mb.Input({ source: new mb.BlobSource(file), formats: mb.ALL_FORMATS })
    try {
      if (!(await input.canRead())) return null
      const [v, a] = await Promise.all([input.getPrimaryVideoTrack(), input.getPrimaryAudioTrack()])
      const duration = await input.getDurationFromMetadata().catch(() => null)
      return {
        audio: a ? await a.getCodec() : null,
        video: v ? await v.getCodec() : null,
        ...(v ? { height: await v.getDisplayHeight() } : {}),
        ...(duration ? { duration } : {}),
      }
    } catch {
      return null
    } finally {
      input.dispose()
    }
  },

  async make(file, plan, opts) {
    const mb = await import('mediabunny')
    const input = new mb.Input({ source: new mb.BlobSource(file), formats: mb.ALL_FORMATS })
    let sink: Sink | null = null
    try {
      if (!(await input.canRead())) return null
      const [pv, pa] = await Promise.all([input.getPrimaryVideoTrack(), input.getPrimaryAudioTrack()])
      if (plan.kind === 'video' ? !pv : !pa) return null
      const audioOnly = plan.kind === 'audio'
      const opus = audioOnly && (await mb.canEncodeAudio('opus'))
      sink = await openSink()
      const output = new mb.Output({
        format: opus ? new mb.OggOutputFormat() : new mb.Mp4OutputFormat({ fastStart: 'fragmented' }),
        target: new mb.StreamTarget(sink.writable, { chunked: true }),
      })
      const firstVideo = async (width: number, height: number) => {
        for (const t of VIDEO_TARGETS) if (plays(t.type) && (await mb.canEncodeVideo(t.codec, { width, height }))) return t.codec
        return 'avc' as const
      }
      const firstAudio = async () => {
        for (const t of AUDIO_TARGETS) if (plays(t.type) && (await mb.canEncodeAudio(t.codec))) return t.codec
        return 'aac' as const
      }
      const conversion = await mb.Conversion.init({
        input,
        output,
        tracks: 'primary',
        showWarnings: false,
        video: audioOnly
          ? { discard: true }
          : async (t) => {
              const height = await t.getDisplayHeight()
              const shrink = plan.maxHeight && height > plan.maxHeight ? plan.maxHeight : undefined
              const codec = await t.getCodecParameterString()
              if (plan.why === 'unplayable' && !shrink && t.codec === 'avc' && codec && plays(`video/mp4; codecs="${codec}"`)) return {}
              const h = shrink ?? height
              const w = Math.round((h * (await t.getDisplayWidth())) / height)
              return {
                codec: await firstVideo(w, h),
                forceTranscode: true,
                quality: new mb.Quality('high'),
                ...(shrink ? { height: shrink } : {}),
              }
            },
        audio: async (t) => {
          const channels = Math.min(2, await t.getNumberOfChannels())
          if (audioOnly) {
            return { codec: opus ? 'opus' : await firstAudio(), forceTranscode: true, numberOfChannels: channels, quality: new mb.Quality({ bitrate: 160_000 }) }
          }
          const codec = await t.getCodecParameterString()
          if (codec && PORTABLE_AUDIO.test(t.codec ?? '') && plays(`audio/mp4; codecs="${codec}"`)) return {}
          return { codec: await firstAudio(), numberOfChannels: channels, quality: new mb.Quality({ bitrate: 192_000 }) }
        },
      })
      // A copy missing its picture or its sound isn't one: that file needs hrth import.
      const lost = conversion.discardedTracks.some((d) => d.reason !== 'discarded_by_user' && (d.track === pv || d.track === pa))
      if (!conversion.isValid || lost) {
        await sink.remove()
        return null
      }
      conversion.onProgress = (p) => opts.onProgress?.(p)
      const stop = () => void conversion.cancel()
      opts.signal?.addEventListener('abort', stop)
      try {
        await conversion.execute()
      } finally {
        opts.signal?.removeEventListener('abort', stop)
      }
      const mime = await output.getMimeType()
      return { blob: await sink.result(), mime, ...(sink.name ? { name: sink.name } : {}) }
    } catch (e) {
      await sink?.remove()
      throw e
    } finally {
      input.dispose()
    }
  },

  async open(name) {
    const dir = await copiesDir()
    try {
      return dir ? await (await dir.getFileHandle(name)).getFile() : null
    } catch {
      return null
    }
  },

  async remove(name) {
    await (await copiesDir())?.removeEntry(name).catch(() => undefined)
  },

  async prune(keep) {
    const dir = await copiesDir()
    if (!dir) return
    const names: string[] = []
    for await (const name of (dir as unknown as { keys(): AsyncIterable<string> }).keys()) names.push(name)
    for (const name of names.filter((n) => !keep.includes(n))) {
      const f = await this.open(name)
      if (f && f instanceof File && Date.now() - f.lastModified > DAY) await this.remove(name)
    }
  },
}
